import { IPropertyContract } from "../zeos-api/contracts/Property";
import { MonitorTypeEnum } from "../zeos-api/enums/Monitor";
import {
  formatDateToISO,
  getTwelveMonthRangeFromEndMonth,
} from "./DateHelpers";

export const DEFAULT_DASHBOARD_TIME_RANGE = "T12";

export const DEFAULT_UNAVAILABLE_TIME_RANGES = ["3YR"];

export const DASHBOARD_TIME_RANGE_OPTIONS: Array<{
  value: string;
  label: string;
}> = [
  { value: "1M", label: "1M" },
  { value: "3M", label: "3M" },
  { value: "6M", label: "6M" },
  { value: "T12", label: "1YR" },
  { value: "3YR", label: "3YR" },
  { value: "YTD", label: "YTD" },
];

export const unavailableTimeRangeSet = (
  unavailableTimeRanges?: string[] | null
) => {
  const values = unavailableTimeRanges ?? DEFAULT_UNAVAILABLE_TIME_RANGES;
  return new Set(values.map((value) => value.toUpperCase()));
};

export const visibleDashboardTimeRangeOptions = (
  unavailableTimeRanges?: string[] | null
) => {
  const blocked = unavailableTimeRangeSet(unavailableTimeRanges);
  return DASHBOARD_TIME_RANGE_OPTIONS.filter(
    (range) => !blocked.has(range.value.toUpperCase())
  );
};

export const coerceDashboardTimeRange = (
  timeRange: string,
  unavailableTimeRanges?: string[] | null
) => {
  const blocked = unavailableTimeRangeSet(unavailableTimeRanges);
  if (!timeRange || blocked.has(timeRange.toUpperCase())) {
    return DEFAULT_DASHBOARD_TIME_RANGE;
  }
  return timeRange;
};

export const hasMonitor = (
  monitorType: MonitorTypeEnum,
  property: IPropertyContract
) => {
  return (
    property?.monitors?.some((m) => m.monitorTypeId === monitorType) ?? false
  );
};

export const getDashboardDateRange = (timeRange: string) => {
  const today = new Date();
  const endDate = new Date(today);
  let startDate = new Date(today);

  switch (timeRange) {
    case "1M":
      startDate.setMonth(endDate.getMonth() - 1);
      break;
    case "3M":
      startDate.setMonth(endDate.getMonth() - 3);
      break;
    case "6M":
      startDate.setMonth(endDate.getMonth() - 6);
      break;
    case "3YR":
      startDate.setFullYear(endDate.getFullYear() - 3);
      break;
    case "YTD":
      startDate = new Date(endDate.getFullYear(), 0, 1);
      break;
    case "T12":
    default: {
      const endOfMonth = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        0
      );
      return getTwelveMonthRangeFromEndMonth(formatDateToISO(endOfMonth));
    }
  }

  return {
    startDate: formatDateToISO(startDate),
    endDate: formatDateToISO(endDate),
  };
};

export const isDashboardOverviewSuccess = <T>(
  zeosStatus?: string | null,
  data?: T | null
): data is T => zeosStatus?.toLowerCase() === "success" && data != null;

export type DashboardOverviewSelection = {
  propertyId: number;
  timeRange: string;
};

/** True when cached overview belongs to the property and range currently on screen. */
export const dashboardOverviewMatchesSelection = (
  overview: DashboardOverviewSelection | undefined | null,
  propertyId: number | undefined,
  timeRange: string
): boolean =>
  overview != null &&
  propertyId != null &&
  overview.propertyId === propertyId &&
  overview.timeRange === timeRange;

/**
 * Keep last-good tiles only for the same property and date range.
 * A failed fetch for a new property (or range) must not keep the previous payload.
 */
export const keepLastGoodDashboardOverview = <
  T extends DashboardOverviewSelection,
>(
  prior: T | undefined,
  propertyId: number | undefined,
  timeRange: string
): T | undefined =>
  dashboardOverviewMatchesSelection(prior, propertyId, timeRange)
    ? prior
    : undefined;

/** Drop a fetched resource when its id does not match the id currently requested. */
export const resourceMatchesRequestedId = (
  resourceId: number | undefined,
  requestedId: number | undefined
): boolean =>
  resourceId != null && requestedId != null && resourceId === requestedId;

/**
 * Keep last-good property/company only when it already belongs to the requested id.
 * A failed fetch for a different id must not keep the previous payload.
 */
export const keepLastGoodByRequestedId = <T>(
  prior: T | undefined,
  priorId: number | undefined,
  requestedId: number | undefined
): T | undefined =>
  resourceMatchesRequestedId(priorId, requestedId) ? prior : undefined;

export const tileTitleMatchesSearch = (
  title: string,
  searchQuery: string
): boolean => {
  const query = searchQuery.trim().toLowerCase();
  if (!query) {
    return true;
  }
  return title.toLowerCase().includes(query);
};
